import { AnimatePresence } from 'framer-motion';
import { MobileHeader } from '../MobileHeader';
import { MobileSettingsSheet } from '../MobileSettingsSheet';
import { DetailView } from '../DetailView';
import { LinkedAccountsPanel, SampleAccount } from '../LinkedAccountsPanel';
import { DiagnosticCard } from '../DiagnosticCard';
import { PortfolioSnapshot } from '../PortfolioSnapshot';
import { InsightsFeed } from '@/components/insights/InsightsFeed';
import { ActionPlanPanel, ActionPlanPanelRef } from '../ActionPlanPanel';
import { TacticalEducationPanel } from '../TacticalEducationPanel';
import { DashboardSidebar } from './DashboardSidebar';
import { AIInsightCard } from '../AIInsightCard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { HoldingsTable } from '../HoldingsTable';
import { EducationHub } from '@/components/education';
import { ModelTab } from '@/components/model';
import { ProfileTab } from '@/components/profile';
import { AccountsTab } from '@/components/accounts';
import { PrivacyTab } from '@/components/privacy';
import { BottomNavigation } from '../BottomNavigation';
import { DIAGNOSTIC_CATEGORIES } from '@/lib/constants';

interface DashboardMobileViewProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
  selectedCategory: string | null;
  selectedCard: any;
  analysis: any;
  clientInfo: any;
  scoringConfig: any;
  checklist: any;
  lifetimeIncomeInputs: any;
  assumptions: any;
  partnerView: string;
  partner: any;
  sampleAccounts: SampleAccount[];
  onSelectCategory: (key: string | null) => void;
  onChecklistUpdate: any;
  onLifetimeIncomeUpdate: any;
  snapshotData: any;
  cardContracts: any[];
  actionPlan: any;
  holdings: any[];
  onHoldingsUpdate: any;
  observationCards: any[];
  noIssuesCards: any[];
  notes: string;
  actionPlanRef: React.RefObject<ActionPlanPanelRef>;
  actionPlanContainerRef: React.RefObject<HTMLDivElement>;
  settingsOpen: boolean;
  setSettingsOpen: (open: boolean) => void;
  adviceModel: any;
  advisorFee: number;
  onClientInfoChange: any;
  onLoadSample: any;
}

export function DashboardMobileView({
  activeTab,
  onTabChange,
  selectedCategory,
  selectedCard,
  analysis,
  clientInfo,
  scoringConfig,
  checklist,
  lifetimeIncomeInputs,
  assumptions,
  partnerView,
  partner,
  sampleAccounts,
  onSelectCategory,
  onChecklistUpdate,
  onLifetimeIncomeUpdate,
  snapshotData,
  cardContracts,
  actionPlan,
  holdings,
  onHoldingsUpdate,
  observationCards,
  noIssuesCards,
  notes,
  actionPlanRef,
  actionPlanContainerRef,
  settingsOpen,
  setSettingsOpen,
  adviceModel,
  advisorFee,
  onClientInfoChange,
  onLoadSample,
}: DashboardMobileViewProps) {

  return (
    <>
      <MobileHeader
        analysis={analysis}
        onSettingsOpen={() => setSettingsOpen(true)}
      />
      <MobileSettingsSheet
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        clientInfo={clientInfo}
        analysis={analysis}
        holdings={holdings}
        notes={notes}
        assumptions={assumptions}
        scoringConfig={scoringConfig}
        adviceModel={adviceModel}
        advisorFee={advisorFee}
        onClientInfoChange={onClientInfoChange}
        onLoadSample={onLoadSample}
      />

      <main className="container mx-auto px-3 py-4">
        {activeTab === 'observations' && (
          <AnimatePresence mode="wait">
            {selectedCategory && (selectedCard || analysis.diagnostics[selectedCategory as keyof typeof analysis.diagnostics]) ? (
              <DetailView
                key="detail-view"
                name={selectedCard?.title ?? DIAGNOSTIC_CATEGORIES[selectedCategory as keyof typeof DIAGNOSTIC_CATEGORIES]?.name ?? selectedCategory}
                categoryKey={selectedCategory}
                result={selectedCard
                  ? {
                      status: selectedCard.status,
                      score: selectedCard.score,
                      keyFinding: selectedCard.keyFinding,
                      headlineMetric: selectedCard.headlineMetric,
                      details: selectedCard.details,
                    }
                  : analysis.diagnostics[selectedCategory as keyof typeof analysis.diagnostics]
                }
                onClose={() => onSelectCategory(null)}
                scoringConfig={scoringConfig}
                riskTolerance={clientInfo.riskTolerance}
                clientAge={clientInfo.currentAge}
                inflationRate={assumptions.inflationRate}
                checklist={checklist}
                onChecklistUpdate={onChecklistUpdate}
                lifetimeIncomeInputs={lifetimeIncomeInputs}
                onLifetimeIncomeUpdate={onLifetimeIncomeUpdate}
                card={selectedCard}
              />
            ) : (
              <div key="observations-view" className="space-y-6 animate-fade-in">
                {/* AI Insight Card - The "Compliment Sandwich" */}
                <AIInsightCard analysis={analysis} className="w-full" />

                {/* Observations Section - Full-page expanded cards */}
                {observationCards.length > 0 && (
                  <div className="space-y-3">
                    <h3 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-status-warning" />
                      Observations
                      <span className="text-xs">({observationCards.length})</span>
                    </h3>
                    <div className="space-y-3">
                      {observationCards.map((c: any, index: number) => (
                        <DiagnosticCard
                          key={String(c.id)}
                          name={String(c.title)}
                          subtitle={c.subtitle}
                          iconName={c.iconName ?? "Shield"}
                          categoryKey={String(c.id)}
                          result={{
                            status: c.status,
                            score: c.score,
                            keyFinding: c.keyFinding,
                            headlineMetric: c.headlineMetric,
                            details: c.details,
                          }}
                          onViewDetails={() => onSelectCategory(String(c.id))}
                          scoringConfig={scoringConfig}
                          riskTolerance={clientInfo.riskTolerance}
                          animationDelay={index}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* No Issues Detected Section */}
                {noIssuesCards.length > 0 && (
                  <div className="space-y-3">
                    <h3 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-status-good" />
                      No Issues Detected
                      <span className="text-xs">({noIssuesCards.length})</span>
                    </h3>
                    <div className="space-y-3">
                      {noIssuesCards.map((c: any, index: number) => (
                        <DiagnosticCard
                          key={String(c.id)}
                          name={String(c.title)}
                          subtitle={c.subtitle}
                          iconName={c.iconName ?? "Shield"}
                          categoryKey={String(c.id)}
                          result={{
                            status: c.status,
                            score: c.score,
                            keyFinding: c.keyFinding,
                            headlineMetric: c.headlineMetric,
                            details: c.details,
                          }}
                          onViewDetails={() => onSelectCategory(String(c.id))}
                          scoringConfig={scoringConfig}
                          riskTolerance={clientInfo.riskTolerance}
                          animationDelay={observationCards.length + index}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Portfolio Snapshot - Summary after cards */}
                <PortfolioSnapshot
                  totalValue={snapshotData.totalValue}
                  accountCount={snapshotData.accountCount}
                  issuesCount={snapshotData.issuesCount}
                  isHouseholdView={partnerView === 'household' && !!partner}
                  partnerName={partner?.name}
                />

                {/* Insights Feed - Key Takeaways */}
                <InsightsFeed
                  cards={cardContracts}
                  onViewCard={(cardId) => onSelectCategory(cardId)}
                />

                {/* Action Plan below insights on mobile */}
                <div ref={actionPlanContainerRef} className="space-y-4">
                  <ActionPlanPanel
                    ref={actionPlanRef}
                    actionPlan={actionPlan}
                    onSelectCategory={(key) => onSelectCategory(key)}
                  />

                  {/* Tactical Education Panel */}
                  <TacticalEducationPanel cards={cardContracts} />
                </div>

                {/* Inline sidebar content on mobile (excluding LinkedAccountsPanel) */}
                <div className="pt-2">
                  <DashboardSidebar excludeLinkedAccounts sampleAccounts={sampleAccounts} />
                </div>
              </div>
            )}
          </AnimatePresence>
        )}

        {activeTab === 'model' && (
          <ModelTab portfolioValue={snapshotData.totalValue} />
        )}

        {activeTab === 'holdings' && (
          <div className="space-y-4">
            {/* Linked Accounts - consolidated in Holdings tab */}
            <LinkedAccountsPanel onHoldingsSync={() => {}} compact sampleAccounts={sampleAccounts} />

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Portfolio Holdings</CardTitle>
              </CardHeader>
              <CardContent className="p-3 pt-0">
                <HoldingsTable holdings={holdings} onUpdate={onHoldingsUpdate} sampleAccounts={sampleAccounts} onNavigateToAccounts={() => onTabChange('accounts')} />
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'learn' && (
          <EducationHub />
        )}

        {activeTab === 'profile' && (
          <ProfileTab />
        )}

        {activeTab === 'accounts' && (
          <AccountsTab sampleAccounts={sampleAccounts} />
        )}

        {activeTab === 'privacy' && (
          <PrivacyTab />
        )}
      </main>

      <BottomNavigation activeTab={activeTab} onTabChange={onTabChange} />

      {/* Chatbot Mount Point - Intercom-style sidebar trigger (Phase 8) */}
      <div
        id="chatbot-mount-point"
        className="fixed bottom-20 right-4 z-40"
        aria-label="AI Assistant"
      >
        {/* ChatbotSidebar will be mounted here in Phase 8 */}
      </div>
    </>
  );
}
